import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  useToast,
} from '@ais-ui/ui-core';
import React, { useEffect, useState } from 'react';
import * as z from 'zod';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettings } from '../SettingsProvider';
import { useSaveSettings } from '@ais-ui/websockets';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  cn,
  CursorPositionFormat,
  DateTimeFormat,
  TimeZone,
  useTimeZoneContext,
} from '@ais-ui/utils';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { useSettingsPage } from '../SettingsPageProvider';

const generalFormSchema = z.object({
  lengthUnit: z.string().optional(),
  speedUnit: z.string().optional(),
  temperatureUnit: z.string().optional(),
  dateFormat: z.string().optional(),
  timeZone: z.string().optional(),
  showCursorPosition: z.boolean().optional(),
  cursorPositionFormat: z.string().optional(),
});

type GeneralFormValues = z.infer<typeof generalFormSchema>;

export const FormattingForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const { timeZone } = useTimeZoneContext();
  const { userDateFormat } = useDateTimeFormat();

  const getTimeZonesPlusLocalUserTimeZone = (): string[] => {
    const predefinedTimeZones = Object.values(TimeZone) as string[];

    if (predefinedTimeZones.includes(timeZone)) {
      return predefinedTimeZones;
    }

    return [...predefinedTimeZones, timeZone];
  };

  const initialValues = {
    lengthUnit: settings['ribbonPlugin.settings'].length_unit,
    speedUnit: settings['ribbonPlugin.settings'].speed_unit,
    temperatureUnit: settings['ribbonPlugin.settings'].temperature_unit,
    dateFormat: userDateFormat,
    timeZone: timeZone,
    showCursorPosition:
      settings['ribbonPlugin.settings'].show_cursor_position || false,
    cursorPositionFormat:
      settings['ribbonPlugin.settings'].cursor_position_format ||
      CursorPositionFormat.positionDDMM,
  };

  const form = useForm<GeneralFormValues>({
    resolver: zodResolver(generalFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: GeneralFormValues) {
    setIsSubmitting(true);

    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        // length_unit: data.lengthUnit,
        // speed_unit: data.speedUnit,
        // temperature_unit: data.temperatureUnit,
        date_format_mp: data.dateFormat,
        time_zone: data.timeZone,
        show_cursor_position: data.showCursorPosition,
        cursor_position_format: data.cursorPositionFormat,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-general-settings-enable-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsGeneralSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-general-settings-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsGeneralFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div>
          <div className="space-y-4">
            <h3 className="text-lg font-medium">
              {messages.settingsGeneralFormattingTitle}
            </h3>
            <div className="space-y-4">
              <FormField
                key="dateFormat"
                control={form.control}
                name="dateFormat"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsGeneralDateFormatLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsGeneralDateFormatDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger
                          id="date-format-dropdown-button"
                          className="w-[240px]"
                        >
                          <SelectValue placeholder="Select a date time format" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(DateTimeFormat).map(
                            ([key, format]) => (
                              <SelectItem
                                key={format}
                                value={format}
                                id={`date-format-${key.toLowerCase()}`}
                              >
                                {format}
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="timeZone"
                control={form.control}
                name="timeZone"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsGeneralTimezoneLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsGeneralTimezoneDescription}
                      </FormDescription>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger
                          id="time-zone-dropdown-button"
                          className="w-[240px]"
                        >
                          <SelectValue placeholder="Select a time zone" />
                        </SelectTrigger>
                        <SelectContent>
                          {getTimeZonesPlusLocalUserTimeZone().map(
                            (timeZone) => (
                              <SelectItem
                                key={timeZone}
                                value={timeZone}
                                id={`time-zone-${timeZone.toLowerCase()}`}
                              >
                                {timeZone}
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                key="showCursorPosition"
                control={form.control}
                name="showCursorPosition"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsGeneralEnableCursorPositionLabel}
                      </FormLabel>
                      <FormDescription>
                        {
                          messages.settingsGeneralEnableCursorPositionDescription
                        }
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="show-cursor-position-switch"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="cursorPositionFormat"
                control={form.control}
                name="cursorPositionFormat"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsGeneralPositionFormatLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsGeneralPositionFormatDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger
                          id="cursor-position-format-dropdown-button"
                          className="w-[240px]"
                        >
                          <SelectValue placeholder="Select cursor position format" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.values(CursorPositionFormat).map((format) => (
                            <SelectItem
                              key={format}
                              value={format}
                              id={`cursor-position-format-${format.toLowerCase()}`}
                            >
                              {format}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-general-formatting-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
